import React from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AssignmentNotification, NotificationType, NotificationState } from "../../../../../types/scheduling/meetings";
import { Congregation, PublicTalkAssignment, Speaker } from "../../../../../types/scheduling/weekend";
import { ISODateString, NotificationDates } from "../../../../../types/date";
import { CongSettings } from "../../../../../types/scheduling/settings";
import { AssignmentSummary } from "../../../RecentAssignments";
import { hasAbsence } from "../../../../../helpers/absence";
import { stringToDate } from "../../../../../helpers/dateHelpers";
import { nameOfUser } from "../../../../../helpers/user";
import { SpeakerName, SpeakerNotOutgoingWarning } from "../../wm_common";
import { NotificationIcon, notificationStatePart } from "../../../notificationStatus";
import { haveAVAAssignment, haveWMCoreAssignment, havePWAssignment, OverlappingAssignment } from "../../../common";

interface OutSpeakerDropdownProps {
  speakers: Speaker[];
  out: PublicTalkAssignment;
  meetingDate: ISODateString;
  absences?: any[];
  selected?: number;
  setSpeaker: (speaker: number) => void;
  saving: boolean;
  notifications?: AssignmentNotification[];
  notificationDates: NotificationDates;
  congSettings: CongSettings;
  userAssignmentMap: Map<number, AssignmentSummary[]>;
  outCongregation?: Congregation;
  pwSchedules: any[];
  className?: string;
}

export const OutSpeakerDropdown: React.FC<OutSpeakerDropdownProps> = (props) => {
  const { t } = useTranslation();

  const speaker = props.speakers.find((s) => s.id === props.selected);
  const speakerDisplayName = speaker?.userId ? nameOfUser(speaker) : undefined;
  const hasConflictingAbsence = speaker?.userId
    ? hasAbsence(speaker.userId, stringToDate(props.meetingDate), props.absences)
    : false;

  const notification = props.notifications?.find((n) => speaker?.userId === n.assignee && props.out.id === n.part);
  const notificationState: NotificationState =
    notificationStatePart(speaker?.userId ?? 0, notification) ?? NotificationState.Created;

  // returns the localized key of the overlapping assignment type, or undefined if there is no overlap
  const hasOverlappingAssignment = (): string | undefined => {
    // see if they have an AVA assignment
    if (!speaker?.userId) return undefined;
    const avaAssignment = haveAVAAssignment(
      speaker.userId,
      props.meetingDate,
      props.congSettings,
      "weekend",
      props.userAssignmentMap,
    );
    if (avaAssignment) {
      return avaAssignment;
    }

    // see if they have any other WM assignment
    if (haveWMCoreAssignment(speaker.userId, props.out.date, props.userAssignmentMap)) return "schedules.weekend.crh";

    if (havePWAssignment(speaker.userId, props.out.date, props.pwSchedules, props.outCongregation)) {
      return "schedules.public-witnessing.title";
    }

    // see if they are also the incoming speaker
    return props.userAssignmentMap
      .get(speaker.userId)
      ?.some((as) => as.type === "publicTalk" && as.date === props.out.date) === true
      ? "schedules.weekend.incoming"
      : undefined;
  };

  const assignmentOverlap = hasOverlappingAssignment();
  const isPastWeek = new Date(props.out.date) < new Date();

  return (
    <Form.Group className={props.className}>
      <Form.Label className="fw-bolder text-muted mb-1">{t("schedules.weekend.speaker.0")}</Form.Label>
      <Dropdown>
        <Dropdown.Toggle
          variant="secondary"
          className="dropdown-bounded"
          disabled={!props.speakers.length || props.saving}
        >
          {props.saving ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <span>
              {!isPastWeek && <NotificationIcon nstate={notificationState} hasAbsence={hasConflictingAbsence} />}
              {!!assignmentOverlap && <OverlappingAssignment className="ms-1" label={t(assignmentOverlap)} />}
            </span>
          )}
          <span className="ms-1 me-1">{speakerDisplayName || <i>{t("general.none-selected")}</i>}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {!!notification && (
            <>
              <NotificationOptions
                startDate={props.notificationDates.start}
                endDate={props.notificationDates.end}
                nstate={notificationState}
                notification={notification}
                notificationType={NotificationType.PTOut}
              />
            </>
          )}
          {props.speakers.length
            ? props.speakers
                .sort((a, b) => {
                  const aName = nameOfUser(a);
                  const bName = nameOfUser(b);
                  return aName.localeCompare(bName);
                })
                .filter((s) => s.id !== speaker?.id)
                .map((s) => {
                  const isAbsent = s.userId
                    ? hasAbsence(s.userId, stringToDate(props.meetingDate), props.absences)
                    : false;
                  return (
                    <Dropdown.Item key={s.id} onClick={() => props.setSpeaker(s.id)} disabled={isAbsent}>
                      <SpeakerName speaker={s} isAbsent={isAbsent} />
                    </Dropdown.Item>
                  );
                })
            : [
                <Dropdown.Item key={0} disabled>
                  {t("schedules.privileges.no-one-with-privilege", {
                    privilege: t("schedules.privileges.public-talks-away"),
                  })}
                </Dropdown.Item>,
              ]}
        </Dropdown.Menu>
      </Dropdown>
      {!!speaker && <SpeakerNotOutgoingWarning speaker={speaker} />}
    </Form.Group>
  );
};

// Notification options component
interface NotificationOptionsProps {
  startDate: string;
  endDate: string;
  nstate: number;
  notification: AssignmentNotification;
  notificationType: NotificationType;
}

export const NotificationOptions: React.FC<NotificationOptionsProps> = (_props) => {
  // This component would need to be implemented based on the original code
  // For now, returning an empty fragment as a placeholder
  return <></>;
};
